import { FORM_VALIDATOR_TYPES } from '../constants';
import { IChangePasswordForm, ILoginForm } from '../interface';

export const SESSION_STORAGE_KEY = {
  ACCESS_TOKEN: 'accessToken',
  REFRESH_TOKEN: 'refreshToken',
  CHANGE_PASSWORD_TOKEN: 'changePasswordToken',
  USER: 'auth-user',
  ORGANIZATION_REGISTRATION_COMPLETE: 'organizationRegistrationComplete',
  PUBLIC_TOKEN: 'publicToken',
  // Short-lived (~5 min) token issued by /auth/login when the user belongs to
  // multiple organizations and must pick one before receiving real tokens.
  TEMP_TOKEN: 'tempToken',
  // CVA-3748: scoped token held only between login and OTP verification.
  TWO_FA_TOKEN: 'twoFaToken',
  PRIVILEGES: 'privilegeNames',
  ROLE: 'userRole',
  REVIEW: 'review',
  CHUNK_RETRY_RELOADED: 'chunk_retry_reloaded',
  ROUTE_ERROR_RELOADED: 'route_error_reloaded',
  GLOBAL_ERROR_BOUNDARY_RELOADED: 'global_error_boundary_reloaded',
  // CVA-4197 join-exam plan 0008. Prefix only — the real key is
  // `${EXAM_ATTEMPT_ANSWERS}:${studentId}:${examSubjectId}`, scoped per
  // attempt (that pair is the DB's own unique key for an attempt) so a
  // shared guardian device never cross-reads a different child's draft.
  EXAM_ATTEMPT_ANSWERS: 'examAttemptAnswers',
};

export const TOKEN_TYPES = {
  ACCESS_TOKEN: 'accessToken' as const,
  PUBLIC_TOKEN: 'publicToken' as const,
  REFRESH_TOKEN: 'refreshToken' as const,
  TEMP_TOKEN: 'tempToken' as const,
  // CVA-3748: short-lived token carrying a login across the 2FA challenge.
  // Lives only as long as the emailed code.
  TWO_FA_TOKEN: 'twoFaToken' as const,
};

export const AUTH_ACTION_CODES = {
  FIRST_LOGIN_PASSWORD_CHANGE_REQUIRED: 'FIRST_LOGIN_PASSWORD_CHANGE_REQUIRED',
  ORGANIZATION_SELECTION_REQUIRED: 'ORGANIZATION_SELECTION_REQUIRED',
  AGREEMENT_SIGN_REQUIRED: 'AGREEMENT_SIGN_REQUIRED',
  TERMS_AND_CONDITIONS_ACCEPT_REQUIRED: 'TERMS_AND_CONDITIONS_ACCEPT_REQUIRED',
  // CVA-3748: the password was accepted but an email OTP is still required.
  TWO_FACTOR_REQUIRED: 'TWO_FACTOR_REQUIRED',
} as const;

/**
 * CVA-3754 / CVA-3757 — machine-readable auth failure codes.
 *
 * The backend sends these alongside a `data` payload carrying
 * `retryAfterSeconds` / `attemptsRemaining`, so the UI can run a countdown and
 * warn before a lock instead of parsing the message text.
 */
export const AUTH_ERROR_CODES = {
  INVALID_CREDENTIALS: 'AUTH_INVALID_CREDENTIALS',
  TEMPORARILY_LOCKED: 'AUTH_TEMPORARILY_LOCKED',
  ACCOUNT_LOCKED: 'AUTH_ACCOUNT_LOCKED',
  TWO_FA_INVALID_CODE: 'TWO_FA_INVALID_CODE',
  TWO_FA_CODE_EXPIRED: 'TWO_FA_CODE_EXPIRED',
} as const;

export type AuthActionCode = (typeof AUTH_ACTION_CODES)[keyof typeof AUTH_ACTION_CODES];

/**
 * Application user roles as returned by the backend at `response.role`
 * on a complete login. Used for role-based routing and access decisions
 * (e.g. students land on a common branded page, guardians are blocked from
 * the staff web portal).
 */
export const USER_ROLES = {
  STUDENT: 'Student',
  PARENT: 'Parent',
  CLASSVEEW_ADMIN: 'CLASSVEEW_ADMIN',
} as const;

export type UserRole = (typeof USER_ROLES)[keyof typeof USER_ROLES];

/**
 * A student's ClassVeew ID is derived as `substr(md5(...), 1, 16)`, i.e.
 * exactly 16 lowercase hexadecimal characters. Students log in with this ID
 * in place of an email address, so the login identifier field accepts either
 * a valid email OR a value matching this pattern.
 */
export const CLASSVEEW_ID_REGEX = /^[0-9a-f]{16}$/;

export const CHANGE_PASSWORD_FORM_INIT: IChangePasswordForm = {
  newPassword: {
    value: '',
    validator: FORM_VALIDATOR_TYPES.PASSWORD,
    error: null as string | null,
    isRequired: true,
    disable: false,
  },
  confirmPassword: {
    value: '',
    validator: FORM_VALIDATOR_TYPES.PASSWORD,
    error: null as string | null,
    isRequired: true,
    disable: false,
  },
};

export const LOGIN_FORM_INIT: ILoginForm = {
  email: {
    // Identifier may be a staff email OR a student's 16-char ClassVeew ID.
    // Use EMAIL_OR_CLASSVEEW_ID validation so a valid email or a well-formed
    // ClassVeew ID passes while arbitrary garbage is rejected client-side.
    // The backend auto-detects and maps a bare ID to a synthetic email.
    value: '',
    validator: FORM_VALIDATOR_TYPES.EMAIL_OR_CLASSVEEW_ID,
    error: null as string | null,
    isRequired: true,
    disable: false,
  },
  password: {
    value: '',
    validator: FORM_VALIDATOR_TYPES.TEXT,
    error: null as string | null,
    isRequired: true,
    disable: false,
  },
  termsAccepted: {
    value: false,
    validator: FORM_VALIDATOR_TYPES.BOOLEAN,
    error: null as string | null,
    isRequired: true,
    disable: false,
  },
};
